import { handleMutationResult } from '@/helpers/toastHelper';
import { useConfirm } from '@/services/confirm/ConfirmationService';
import { ReadRoster, useUpdateRosterMutation } from '@/services/gql/graphql.generated';
import { useCallback, useEffect } from 'react';
import { useSelectedRoster } from '../providers/RosterOptionsService';
import { useWorkingRoster } from '../providers/WorkingRosterService';
import strings from '../roster.translations';
import { useUpdateStatusAndTimestamps, useWorkingRosterToGqlRoster } from '../rosterHelper';

export const useSaveRoster = () => {
  const { workingRoster, setWorkingRoster } = useWorkingRoster();
  const selectedRoster = useSelectedRoster();
  const [{ fetching, error }, update] = useUpdateRosterMutation();

  const { unregisterForConfirmation, saveNeeded, registerAction } = useConfirm(state => ({
    unregisterForConfirmation: state.unregisterForConfirmation,
    saveNeeded: state.confirmationNeeded,
    registerAction: state.registerConfirmAction
  }));

  const workingRosterToGqlRoster = useWorkingRosterToGqlRoster();
  const updateStatusAndTimestamps = useUpdateStatusAndTimestamps();

  const saveRoster = useCallback(async () => {
    if (!workingRoster || !selectedRoster) {
      throw Error('Roster must be defined');
    }

    const result = await update({
      updateRoster: workingRosterToGqlRoster(workingRoster, selectedRoster as ReadRoster)
    });

    await handleMutationResult(result, {
      successMessage: strings.save.success,
      errorMessage: 'Error!',
      onSuccess: data => {
        if (!data?.updateRoster) return;

        // merge status and timestamps from the result of the updateRoster mutation
        const newWorkingRoster = { ...workingRoster };
        updateStatusAndTimestamps(newWorkingRoster, data.updateRoster);

        setWorkingRoster(newWorkingRoster);
        unregisterForConfirmation();
      }
    });

    return result;
  }, [
    workingRoster,
    setWorkingRoster,
    updateStatusAndTimestamps,
    unregisterForConfirmation,
    workingRosterToGqlRoster,
    update,
    selectedRoster
  ]);

  useEffect(() => {
    if (saveNeeded) {
      registerAction({
        callback: saveRoster,
        label: strings.saveAndLeave
      });
    }
  }, [saveNeeded, saveRoster, registerAction]);

  return { saveRoster, error, fetching };
};
